export const IMAGE_INPUT_TYPE = Object.freeze({
  PHOTO: 'PHOTO',
  INK: 'INK',
});

export const TEXT_INPUT_TYPE = 'TEXT';

export function assertImageInputType(inputType) {
  if (inputType === IMAGE_INPUT_TYPE.PHOTO || inputType === IMAGE_INPUT_TYPE.INK) {
    return inputType;
  }
  throw new Error('Missing input type.');
}

export function normalizeImageInputType(inputType) {
  return assertImageInputType(inputType);
}

export function buildImageSubmissionFormData({
  file,
  files,
  inputType,
  regionBbox,
  timeSpentSeconds,
  imageUrl,
  imageUrls,
} = {}) {
  const normalizedFiles = Array.isArray(files)
    ? files.filter(Boolean)
    : file
    ? [file]
    : [];
  const normalizedImageUrlsRaw = Array.isArray(imageUrls)
    ? imageUrls.map((url) => (typeof url === 'string' ? url.trim() : ''))
    : [];
  const normalizedImageUrls = normalizedImageUrlsRaw.filter(Boolean);
  const hasImageUrl = typeof imageUrl === 'string' && imageUrl.trim();

  if (
    process.env.NODE_ENV !== 'production' &&
    normalizedFiles.length > 1 &&
    normalizedImageUrlsRaw.length > 0
  ) {
    const hasEmptyUrl = normalizedImageUrlsRaw.some((url) => !url);
    if (normalizedImageUrlsRaw.length !== normalizedFiles.length || hasEmptyUrl) {
      throw new Error(
        'Invalid multi-file image_urls: expected one non-empty URL per file in matching order.'
      );
    }
  }

  if (normalizedFiles.length === 0 && normalizedImageUrls.length === 0 && !hasImageUrl) {
    throw new Error('Provide at least one image file or image URL.');
  }

  const validatedInputType = assertImageInputType(inputType);
  const formData = new FormData();

  // Guard: never omit input_type; backend will 422 if missing.
  formData.append('input_type', validatedInputType);

  normalizedFiles.forEach((nextFile) => {
    formData.append('files', nextFile);
  });
  if (normalizedImageUrls.length > 0) {
    formData.append('image_urls', JSON.stringify(normalizedImageUrls));
  }
  if (hasImageUrl) {
    formData.append('image_url', imageUrl.trim());
  }
  if (regionBbox !== undefined && regionBbox !== null) {
    if (typeof regionBbox === 'string') {
      formData.append('region_bbox', regionBbox);
    } else {
      formData.append('region_bbox', JSON.stringify(regionBbox));
    }
  }
  if (typeof timeSpentSeconds === 'number' && Number.isFinite(timeSpentSeconds)) {
    formData.append('time_spent_seconds', String(timeSpentSeconds));
  }

  return formData;
}

export function buildTextSubmissionFormData({ answerText, timeSpentSeconds } = {}) {
  const normalizedAnswer = typeof answerText === 'string' ? answerText.trim() : '';
  if (!normalizedAnswer) {
    throw new Error('Provide a typed answer.');
  }

  const formData = new FormData();
  formData.append('input_type', TEXT_INPUT_TYPE);
  formData.append('answer_text', normalizedAnswer);
  if (typeof timeSpentSeconds === 'number' && Number.isFinite(timeSpentSeconds)) {
    formData.append('time_spent_seconds', String(timeSpentSeconds));
  }
  return formData;
}

export function appendImageFormData(formData, { file, inputType } = {}) {
  if (!(formData instanceof FormData)) {
    throw new Error('appendImageFormData requires a FormData instance.');
  }
  if (Array.isArray(file)) {
    file.filter(Boolean).forEach((nextFile) => {
      formData.append('files', nextFile);
    });
  } else if (file) {
    formData.append('files', file);
  }

  // Guard: never omit input_type; backend will 422 if missing.
  formData.append('input_type', assertImageInputType(inputType));
}
